/**
 * useSession — client-side hook to read session public data from cookie.
 *
 * Client-side session hook — reads session from cookie.
 */
"use client"

import { useState, useEffect } from "react"

interface SessionData {
  userId: string | null
  role: string | null
  isLoading: boolean
  [key: string]: any
}

const COOKIE_PREFIX = "playerdash"
const PUBLIC_DATA_COOKIE = `${COOKIE_PREFIX}_sPublicDataToken`

function readPublicDataFromCookie(): Record<string, any> | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(new RegExp(`${PUBLIC_DATA_COOKIE}=([^;]+)`))
  if (!match) return null
  try {
    return JSON.parse(atob(decodeURIComponent(match[1])))
  } catch {
    return null
  }
}

export function useSession(options?: { initialPublicData?: any }): SessionData {
  const [session, setSession] = useState<SessionData>(() => {
    const pd = options?.initialPublicData || readPublicDataFromCookie()
    if (pd) {
      return {
        ...pd,
        userId: pd.userId ?? null,
        role: pd.role ?? null,
        isLoading: false,
      }
    }
    return { userId: null, role: null, isLoading: true }
  })

  useEffect(() => {
    const pd = readPublicDataFromCookie()
    if (pd) {
      setSession({
        ...pd,
        userId: pd.userId ?? null,
        role: pd.role ?? null,
        isLoading: false,
      })
    } else {
      setSession((s) => ({ ...s, isLoading: false }))
    }
  }, [])

  return session
}
